/**
 * forecast/quarters.ts — quick-261001-m3v.
 *
 * THE FIVE QUARTER TABS the Combined Revenue Forecast shows at all times
 * (John, 2026-10-01: "for each fiscal year, keep all 4 tabs. one for q1, q2,
 * q3, and q4. show the previous tabs as closed … and then have q1 of the next
 * year showing on all as well. so there are 5 tabs on each page at all times.
 * once jan 1 hits, the 5 tabs will update to show q1, q2, q3, q4 of 2027 and
 * q1 of 2028").
 *
 * Derived from `now`, never hard-coded, so the roll on 1 Jan is automatic on
 * every surface that reads this: the API route (which labels it accepts), the
 * SPA tab bar, the page's default tab, and the CEO artifact's mirror of the
 * same rule. Calendar-year fiscal, UTC basis — the same derivation as
 * sync-worker/src/cache/fiscal.ts and the API reader's resolvePeriodLabel.
 *
 * Browser-safe: no Node imports. The SPA aliases this module to source (see
 * spa-cro vite.config.ts) exactly as it does forecast/weighting.
 */

/** A canonical quarter period label, e.g. 'FY2026-Q4'. */
export type ForecastQuarterLabel = `FY${number}-Q${number}`;

/** Where a quarter tab sits relative to today. */
export type ForecastQuarterState = 'closed' | 'current' | 'next';

export interface ForecastQuarterTab {
  label: ForecastQuarterLabel;
  fiscal_year: number;
  fiscal_quarter: number;
  /** 'closed' = a past quarter of the current FY; 'current' = the quarter `now` is in; 'next' = Q1 of the next FY or the following quarter. */
  state: ForecastQuarterState;
  /** Tab caption, e.g. 'Q4 FY2026'. */
  title: string;
}

const LABEL_RE = /^FY(\d{4})-Q([1-4])$/;

export function forecastQuarterLabel(fy: number, fq: number): ForecastQuarterLabel {
  return `FY${String(fy)}-Q${String(fq)}` as ForecastQuarterLabel;
}

export function isForecastQuarterLabel(s: unknown): s is ForecastQuarterLabel {
  return typeof s === 'string' && LABEL_RE.test(s);
}

/** Parse 'FY2026-Q4' → { fy: 2026, fq: 4 }; null when not a quarter label. */
export function parseForecastQuarterLabel(s: string): { fy: number; fq: number } | null {
  const m = LABEL_RE.exec(s);
  if (!m) return null;
  return { fy: Number(m[1]), fq: Number(m[2]) };
}

/** Fiscal (year, quarter) of `now` — calendar year, UTC. */
export function currentForecastQuarter(now: Date): { fy: number; fq: number } {
  return { fy: now.getUTCFullYear(), fq: Math.floor(now.getUTCMonth() / 3) + 1 };
}

/**
 * The five tabs for `now`: Q1..Q4 of the current fiscal year, then Q1 of the
 * next. Quarters before the current one are 'closed'; the current one is
 * 'current'; everything after is 'next'. Order is chronological.
 */
export function forecastQuarterWindow(now: Date): ForecastQuarterTab[] {
  const { fy, fq } = currentForecastQuarter(now);
  const tabs: ForecastQuarterTab[] = [];
  for (let q = 1; q <= 4; q++) {
    tabs.push({
      label: forecastQuarterLabel(fy, q),
      fiscal_year: fy,
      fiscal_quarter: q,
      state: q < fq ? 'closed' : q === fq ? 'current' : 'next',
      title: `Q${String(q)} FY${String(fy)}`,
    });
  }
  tabs.push({
    label: forecastQuarterLabel(fy + 1, 1),
    fiscal_year: fy + 1,
    fiscal_quarter: 1,
    state: 'next',
    title: `Q1 FY${String(fy + 1)}`,
  });
  return tabs;
}

/** The tab for a label, when it is one of the five in view for `now`. */
export function forecastQuarterTabFor(
  label: string,
  now: Date,
): ForecastQuarterTab | undefined {
  return forecastQuarterWindow(now).find((t) => t.label === label);
}

/** The current quarter's tab (the page's default). */
export function currentForecastQuarterTab(now: Date): ForecastQuarterTab {
  const tab = forecastQuarterWindow(now).find((t) => t.state === 'current');
  // Unreachable: the window always contains the current quarter.
  if (!tab) throw new Error('forecastQuarterWindow carried no current quarter');
  return tab;
}

/** The quarter after the current one (Q4 → Q1 of the next FY). */
export function nextForecastQuarterTab(now: Date): ForecastQuarterTab {
  const { fy, fq } = currentForecastQuarter(now);
  const label = fq === 4 ? forecastQuarterLabel(fy + 1, 1) : forecastQuarterLabel(fy, fq + 1);
  const tab = forecastQuarterWindow(now).find((t) => t.label === label);
  if (!tab) throw new Error('forecastQuarterWindow carried no next quarter');
  return tab;
}
